import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Spinner, Alert, Button } from 'react-bootstrap';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import ScrollToTopButton from '../../components/ScrollToTopButton/ScrollToTopButton';
import { auth } from '../../firebase';
import './ChapterView.css';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

const ChapterView = () => {
  const { book_id, chapter_id } = useParams();
  const navigate = useNavigate();

  const [chapter, setChapter] = useState(null);
  const [book, setBook] = useState(null);
  const [prevChapter, setPrevChapter] = useState(null);
  const [nextChapter, setNextChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchChapterData = async () => {
      try {
        setLoading(true);
        setError(null);

        const user = auth.currentUser;
        if (!user) {
          throw new Error("User not authenticated.");
        }
        const token = await user.getIdToken();

        // Fetch the full comic details to get the list of all chapters
        const bookResponse = await fetch(`${backendUrl}/api/comics/${book_id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-API-Key': process.env.REACT_APP_API_KEY
          }
        });
        if (!bookResponse.ok) throw new Error('Could not find the requested comic book.');
        const foundBook = await bookResponse.json();
        setBook(foundBook);

        // Find the current chapter and its index
        const currentChapterIndex = foundBook.chapters.findIndex(ch => ch.id === chapter_id);
        if (currentChapterIndex === -1) throw new Error('Could not find the requested chapter.');

        const foundChapter = foundBook.chapters[currentChapterIndex];

        // Set previous and next chapters for navigation
        setPrevChapter(currentChapterIndex > 0 ? foundBook.chapters[currentChapterIndex - 1] : null);
        setNextChapter(currentChapterIndex < foundBook.chapters.length - 1 ? foundBook.chapters[currentChapterIndex + 1] : null);

        // Sort pages numerically, handling both local filenames and remote absolute URLs
        const getPageNumber = (url) => {
          if (url.startsWith('http')) {
            const filename = url.split('/').pop() || '';
            const match = filename.match(/(\d+)/);
            return match ? parseInt(match[0], 10) : 0;
          }
          const match = url.match(/(\d+)/);
          return match ? parseInt(match[0], 10) : 0;
        };

        const sortedPages = [...foundChapter.pages].sort((a, b) => {
          return getPageNumber(a) - getPageNumber(b);
        });

        setChapter({ ...foundChapter, pages: sortedPages });
      } catch (err) {
        setError(err.message || 'An unexpected error occurred.');
      } finally {
        setLoading(false);
      }
    };

    window.scrollTo(0, 0);
    fetchChapterData();
  }, [book_id, chapter_id]);

  const handlePrevChapter = () => {
    if (prevChapter) {
      navigate(`/comic/${book_id}/chapter/${prevChapter.id}`);
    }
  };

  const handleNextChapter = () => {
    if (nextChapter) {
      navigate(`/comic/${book_id}/chapter/${nextChapter.id}`);
    }
  };

  if (loading) return <div className="loading-container"><Spinner /></div>;
  if (error) return <div className="error-container"><Alert variant="danger">{error}</Alert></div>;
  if (!chapter || !book) return null;

  return (
    <>
      <ScrollToTopButton />
      <Container fluid className="px-0">
        <h2 className="text-center my-4">{book.title} - {chapter.title}</h2>
        <div className="d-flex flex-column align-items-center">
          {chapter.pages.map((page, index) => {
            const pageUrl = page.startsWith('http')
              ? `${backendUrl}/api/images/proxy?url=${encodeURIComponent(page)}`
              : `${backendUrl}/comics/${chapter.folder}/${chapter.id}/${page}`;
            return (
              <LazyLoadImage
                key={`${chapter_id}-${page}`}
                src={pageUrl}
                alt={`Page ${index + 1} of ${chapter.title}`}
                effect="blur"
                className="chapter-page non-interactive my-0 img-fluid"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            );
          })}
        </div>
      </Container>

      <Container className="mt-5 mb-20">
        <div className="d-flex justify-content-between">
          {prevChapter ? (
            <Button variant="outline-primary" size="xs" onClick={handlePrevChapter}>
              &larr; {prevChapter.title.split('-')[0]}
            </Button>
          ) : (
            <div />
          )}

          {nextChapter ? (
            <Button variant="outline-primary" size="xs" onClick={handleNextChapter}>
              {nextChapter.title.split('-')[0]} &rarr;
            </Button>
          ) : (
            <p className="text-muted align-self-center">You've reached the last chapter.</p>
          )}
        </div>
      </Container>
    </>
  );
};

export default ChapterView;
